import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FactureList } from "../../components/factures/FactureList";
import { Plus, Filter, Download, Upload } from "lucide-react";
import { FactureService } from "../../services/factures/factureService";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface Facture {
  id: string;
  numero: string;
  fournisseur_nom: string;
  montant_ttc: number;
  date_emission: string;
  date_echeance: string;
  statut: string;
  score_decision?: number;
}

export const ClientFactures: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id: factureIdFromUrl } = useParams<{ id: string }>();
  const [factures, setFactures] = useState<Facture[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFacture, setSelectedFacture] = useState<Facture | null>(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [comment, setComment] = useState("");
  const [processingAction, setProcessingAction] = useState(false);
  const [stats, setStats] = useState({
    en_attente: 0,
    validees: 0,
    volume_total: 0,
    taux_validation: 0
  });

  const fetchFactures = async () => {
    try {
      setLoading(true);
      const [response, statsResponse] = await Promise.all([
        FactureService.getFactures(),
        FactureService.getFactureStats()
      ]);
      
      setFactures(response.data.results);
      
      if (statsResponse.data) {
          const s = statsResponse.data;
          // Calculate derived stats if not provided directly
          const total = s.total || 1;
          const validee = s.par_statut?.validee?.count || 0;
          
          setStats({
              en_attente: s.par_statut?.soumise?.count || 0,
              validees: validee,
              volume_total: s.volume_total || 0,
              taux_validation: Math.round((validee / total) * 100)
          });
      }
    } catch (error) {
      console.error("Erreur chargement factures", error);
      toast.error(t('factures.management.errorValidation')); // Reuse
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFactures();
  }, []);

  const handleFactureClick = (facture: Facture) => {
    setSelectedFacture(facture);
    setShowValidationModal(true);
    setComment("");
  };

  // Lien direct /client/factures/:id (ex. depuis une notification) : ouvre la facture
  useEffect(() => {
    if (!factureIdFromUrl || loading) return;
    const found = factures.find((f) => String(f.id) === factureIdFromUrl);
    if (found) {
      handleFactureClick(found);
      return;
    }
    FactureService.getFacture(factureIdFromUrl)
      .then((res) => handleFactureClick(res.data))
      .catch(() => {
        toast.error(t('factures.management.notFound'));
        navigate("/client/factures", { replace: true });
      });
  }, [factureIdFromUrl, loading]);

  // Fermer la facture ouverte par lien direct ramène à la liste
  useEffect(() => {
    if (!showValidationModal && factureIdFromUrl && selectedFacture) {
      navigate("/client/factures", { replace: true });
    }
  }, [showValidationModal]);

  const handleValidate = async () => {
    if (!selectedFacture) return;
    
    try {
      setProcessingAction(true);
      await FactureService.validerFacture(selectedFacture.id);
      toast.success(`${t('factures.title')} ${selectedFacture.numero} ${t('factures.management.successValidated')}`);
      setShowValidationModal(false);
      fetchFactures(); // Refresh list
    } catch (error) {
      console.error("Erreur validation", error);
      toast.error(t('factures.management.errorValidation'));
    } finally {
      setProcessingAction(false);
    }
  };

  const handleReject = async () => {
    if (!selectedFacture) return;
    
    if (!comment) {
        toast.error(t('factures.management.errorCommentRequired'));
        return;
    }

    try {
      setProcessingAction(true);
      await FactureService.rejeterFacture(selectedFacture.id, comment);
      toast.success(`${t('factures.title')} ${selectedFacture.numero} ${t('factures.management.successRejected')}`);
      setShowValidationModal(false);
      fetchFactures();
    } catch (error) {
      console.error("Erreur rejet", error);
      toast.error(t('factures.management.errorReject'));
    } finally {
      setProcessingAction(false);
    }
  };

  if (loading && factures.length === 0) {
      return (
          <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
      );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('factures.clientPages.manageTitle')}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {t('factures.clientPages.manageOverview')}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
            <Upload className="h-4 w-4 mr-2" />
            {t('factures.clientPages.import')}
          </button>
          <button
            onClick={() => navigate("/client/factures/new")}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('factures.clientPages.newInvoice')}
          </button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3 rounded-lg bg-primary-100">
                  <Filter className="h-6 w-6 text-primary-600" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('dashboard.stats.pending')}
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900">{stats.en_attente}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3 rounded-lg bg-success-100">
                  <Download className="h-6 w-6 text-success-600" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('factures.clientPages.validatedThisMonth')}
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900">{stats.validees}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3 rounded-lg bg-warning-100">
                  <span className="text-2xl font-bold text-warning-600">
                    XOF
                  </span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('dashboard.stats.totalVolume')}
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900">
                     {new Intl.NumberFormat(i18n.language.startsWith('fr') ? 'fr-FR' : 'en-US', {
                        maximumFractionDigits: 0,
                        notation: "compact", 
                        compactDisplay: "short"
                      }).format(stats.volume_total)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3 rounded-lg bg-primary-100">
                  <span className="text-2xl font-bold text-primary-600">%</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('dashboard.client.validationRate')}
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900">{stats.taux_validation}%</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des factures */}
      <FactureList factures={factures} onFactureClick={handleFactureClick} />

      {/* Modal de validation */}
      {showValidationModal && selectedFacture && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
              onClick={() => setShowValidationModal(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {selectedFacture.statut === 'soumise' ? t('factures.clientPages.validationTitle') : t('factures.clientPages.detailsTitle')}
                    </h3>
                    <div className="mt-4 space-y-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm font-medium text-gray-500">
                          {t('factures.title')}
                        </p>
                        <p className="text-lg font-semibold text-gray-900">
                          {selectedFacture.numero}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-500">
                            {t('factures.supplier')}
                          </p>
                          <p className="text-gray-900">
                            {selectedFacture.fournisseur_nom}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">
                            {t('factures.amount')}
                          </p>
                          <p className="text-lg font-semibold text-gray-900">
                            {new Intl.NumberFormat(i18n.language.startsWith('fr') ? 'fr-FR' : 'en-US', {
                              style: "currency",
                              currency: "XOF",
                            }).format(selectedFacture.montant_ttc)}
                          </p>
                        </div>
                      </div>
                      
                      {selectedFacture.statut === 'soumise' ? (
                        <div>
                          <label
                            htmlFor="comment"
                            className="block text-sm font-medium text-gray-700"
                          >
                            {t('factures.clientPages.optionalComment')}
                          </label>
                          <textarea
                            id="comment"
                            rows={3}
                            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            placeholder={t('factures.clientPages.commentPlaceholder')}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                          />
                        </div>
                      ) : (
                        <div>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {t('factures.status')}: {selectedFacture.statut}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                {selectedFacture.statut === 'soumise' ? (
                  <>
                    <button
                      type="button"
                      onClick={handleValidate}
                      disabled={processingAction}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-success-600 text-base font-medium text-white hover:bg-success-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-success-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      {processingAction ? "..." : t('factures.management.validate')}
                    </button>
                    <button
                      type="button"
                      onClick={handleReject}
                      disabled={processingAction}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      {processingAction ? "..." : t('factures.management.reject')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowValidationModal(false)}
                      disabled={processingAction}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      {t('common.cancel')}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowValidationModal(false)}
                    disabled={processingAction}
                    className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm"
                  >
                    {t('common.close')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
